import { useState } from 'react';
import { Avatar, Box, IconButton, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import rapchikLogo from '../../assets/rapchik-logo.png';
import defaultUserAvatar from '../../assets/default-user-avatar.png';
import headerCloseIcon from '../../assets/header-close-icon.svg';
import headerCloseRing from '../../assets/header-close-ring.svg';
import { getUserProfile } from '../../storage/userProfileStorage';
import ProfileDrawer from './ProfileDrawer';

export { defaultUserAvatar };

function getAvatarSrc() {
  const profile = getUserProfile();
  return (
    profile?.profilePic ||
    profile?.avatarUrl ||
    profile?.avatar ||
    profile?.profileImage ||
    profile?.imageUrl ||
    null
  );
}

function ProfileAvatar({ size = 70, iconSize = 38, onClick, avatarSrc }) {
  const resolvedSrc = avatarSrc ?? getAvatarSrc() ?? defaultUserAvatar;

  return (
    <Avatar
      src={resolvedSrc}
      onClick={onClick}
      alt="User profile"
      sx={{
        width: size,
        height: size,
        bgcolor: '#ffceca',
        border: '1px solid #ff00f2',
        flexShrink: 0,
        cursor: onClick ? 'pointer' : 'default',
        overflow: 'hidden',
        '& .MuiAvatar-img': {
          objectFit: 'cover',
          objectPosition: 'center top',
        },
      }}
    >
      {!resolvedSrc ? <PersonIcon sx={{ color: '#822AFF', fontSize: iconSize }} /> : null}
    </Avatar>
  );
}

function ProfileMenuAvatar({ size = 70, iconSize = 38, onOpenDrawer, avatarSrc }) {
  if (!onOpenDrawer) {
    return <ProfileAvatar size={size} iconSize={iconSize} avatarSrc={avatarSrc} />;
  }

  return (
    <ProfileAvatar
      size={size}
      iconSize={iconSize}
      avatarSrc={avatarSrc}
      onClick={onOpenDrawer}
    />
  );
}

function MobileBazaarHeader({ onClose }) {
  const navigate = useNavigate();

  const handleClose = () => {
    if (onClose) {
      onClose();
      return;
    }
    navigate('/');
  };

  return (
    <Box
      sx={{
        display: { xs: 'flex', md: 'none' },
        width: { xs: 'calc(100% + 19.2px)', sm: 'calc(100% + 32px)' },
        mx: { xs: -1.2, sm: -2 },
        minHeight: 68,
        px: 2.5,
        py: 1.25,
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid #FFF',
        bgcolor: 'rgba(255, 255, 255, 0.90)',
        boxShadow: '0 4px 12px 0 rgba(255, 0, 229, 0.25)',
        backdropFilter: 'blur(4.25px)',
        zIndex: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          maxWidth: 400,
          minHeight: 44,
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontFamily: 'Inter, sans-serif',
            background: 'linear-gradient(180deg, #FF00F2 0%, #822AFF 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 800,
            fontSize: { xs: 36, sm: 44 },
            lineHeight: { xs: '40px', sm: '44px' },
            letterSpacing: '2.5px',
            whiteSpace: 'nowrap',
          }}
        >
          RapBazaar
        </Typography>

        <IconButton
          onClick={handleClose}
          aria-label="Close RapBazaar"
          sx={{
            width: 36,
            height: 36,
            p: 0,
            borderRadius: '100px',
            background: 'linear-gradient(0deg, #5A04D7 0%, #FF00F2 100%)',
            flexShrink: 0,
            '&:hover': {
              background: 'linear-gradient(0deg, #5A04D7 0%, #FF00F2 100%)',
              opacity: 0.92,
            },
          }}
        >
          <Box sx={{ position: 'relative', width: 24, height: 24 }}>
            <Box
              component="img"
              src={headerCloseRing}
              alt=""
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
            />
            <Box
              component="img"
              src={headerCloseIcon}
              alt=""
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 12,
                height: 12,
              }}
            />
          </Box>
        </IconButton>
      </Box>
    </Box>
  );
}

function DesktopHeader({ showSubtitle, hideAvatar = false, onOpenProfileDrawer, avatarSrc }) {
  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        width: '100%',
        maxWidth: '1820px',
        minWidth: '750px',
        height: 110,
        p: '20px',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        mx: 'auto',
        mt: 2,
        borderRadius: '500px',
        border: '2px solid #FFF',
        bgcolor: 'rgba(255, 255, 255, 0.90)',
        boxShadow: '0 4px 12px 0 rgba(255, 0, 229, 0.25)',
        backdropFilter: 'blur(4.25px)',
        zIndex: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: 119,
          px: '12px',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          gap: '10px',
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={rapchikLogo}
          alt="Rapchik logo"
          sx={{
            width: 94.648,
            height: 70,
            display: 'block',
            objectFit: 'contain',
          }}
        />
      </Box>

      <Typography
        component="h1"
        sx={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          fontFamily: 'Inter, sans-serif',
          background: 'linear-gradient(180deg, #FF00F2 0%, #822AFF 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          fontWeight: 800,
          fontSize: 44,
          lineHeight: '44px',
          letterSpacing: '2.5px',
          whiteSpace: 'nowrap',
          textAlign: 'center',
        }}
      >
        RapBazaar
        {showSubtitle ? (
          <Typography
            component="span"
            sx={{
              display: 'block',
              color: '#000066',
              fontSize: 20,
              lineHeight: '24px',
              fontWeight: 600,
              letterSpacing: '0.8px',
              mt: 0.25,
              WebkitTextFillColor: '#000066',
            }}
          >
            Rewards Marketplace
          </Typography>
        ) : null}
      </Typography>

      {!hideAvatar ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: 118, flexShrink: 0 }}>
          <ProfileMenuAvatar size={70} iconSize={38} onOpenDrawer={onOpenProfileDrawer} avatarSrc={avatarSrc} />
        </Box>
      ) : null}
    </Box>
  );
}

function MobileProfileHeader({ showSubtitle, onOpenProfileDrawer, avatarSrc }) {
  return (
    <Box
      sx={{
        display: { xs: 'flex', md: 'none' },
        width: '100%',
        minHeight: 68,
        p: '10px',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        mx: 'auto',
        mt: 0.5,
        borderRadius: '500px',
        border: '2px solid #FFF',
        bgcolor: 'rgba(255, 255, 255, 0.90)',
        boxShadow: '0 4px 12px 0 rgba(255, 0, 229, 0.25)',
        backdropFilter: 'blur(4.25px)',
        zIndex: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: 82,
          px: '12px',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={rapchikLogo}
          alt="Rapchik logo"
          sx={{ width: 54, height: 'auto', display: 'block', objectFit: 'contain' }}
        />
      </Box>

      <Typography
        component="h1"
        sx={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          fontFamily: 'Inter, sans-serif',
          background: 'linear-gradient(180deg, #FF00F2 0%, #822AFF 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          fontWeight: 800,
          fontSize: 24,
          lineHeight: 1,
          letterSpacing: '0.8px',
          whiteSpace: 'nowrap',
          textAlign: 'center',
        }}
      >
        RapBazaar
        {showSubtitle ? (
          <Typography
            component="span"
            sx={{
              display: 'block',
              color: '#000066',
              fontSize: 10,
              lineHeight: 1.1,
              fontWeight: 600,
              letterSpacing: '0.2px',
              mt: 0.1,
              WebkitTextFillColor: '#000066',
            }}
          >
            Rewards Marketplace
          </Typography>
        ) : null}
      </Typography>

      <ProfileMenuAvatar size={42} iconSize={24} onOpenDrawer={onOpenProfileDrawer} avatarSrc={avatarSrc} />
    </Box>
  );
}

export default function AppHeader({
  showSubtitle = false,
  endVariant = 'bazaar',
  onClose,
  onProfileClick,
  onLogoutClick = onProfileClick,
  avatarSrc,
}) {
  const isBazaar = endVariant === 'bazaar';
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const openProfileDrawer = onLogoutClick ? () => setProfileDrawerOpen(true) : undefined;

  return (
    <>
      {isBazaar ? <MobileBazaarHeader onClose={onClose} /> : null}
      {!isBazaar ? (
        <MobileProfileHeader
          showSubtitle={showSubtitle}
          onOpenProfileDrawer={openProfileDrawer}
          avatarSrc={avatarSrc}
        />
      ) : null}
      <DesktopHeader
        showSubtitle={showSubtitle}
        onOpenProfileDrawer={openProfileDrawer}
        avatarSrc={avatarSrc}
      />
      {onLogoutClick ? (
        <ProfileDrawer
          open={profileDrawerOpen}
          onClose={() => setProfileDrawerOpen(false)}
          onLogoutClick={onLogoutClick}
        />
      ) : null}
    </>
  );
}
